/**
 * Provides a focused cross-page override surface so reviewers can stage one
 * schedule for related sports assets without replacing untouched card edits.
 */
import React from 'react';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import type {
  BulkScheduleGroup,
  Schedule,
} from 'src/utils/groupAssetsForBulkSchedule';
import {
  getInitialSchedule,
  getScheduleValidationError,
  isScheduleDirty,
} from './BulkScheduleGroupRow';
import {
  BulkScheduleTitleSectionView,
  type BulkScheduleTitleSection,
} from './BulkScheduleTitleSectionView';

export interface BulkScheduleOverrideModalProps {
  readonly open: boolean;
  readonly groups: readonly BulkScheduleGroup[];
  readonly onApply: (dirty: Map<string, Schedule>) => void;
  readonly onClose: () => void;
}

interface MutableTitleSection {
  readonly eventEnd?: string;
  readonly eventStart?: string;
  readonly groups: BulkScheduleGroup[];
  readonly gti: string;
  readonly titleName: string;
}

interface DirtySummary {
  readonly assetCount: number;
  readonly schedules: Map<string, Schedule>;
}

function createEditorSchedules (
  groups: readonly BulkScheduleGroup[],
): Map<string, Schedule> {
  return new Map(
    groups.map((group) => [group.groupId, getInitialSchedule(group)]),
  );
}

function groupByTitle (
  groups: readonly BulkScheduleGroup[],
): BulkScheduleTitleSection[] {
  const sections = new Map<string, MutableTitleSection>();
  groups.forEach((group) => {
    const section = sections.get(group.gti);
    if (section) {
      section.groups.push(group);
      return;
    }
    sections.set(group.gti, {
      eventEnd: group.eventEnd,
      eventStart: group.eventStart,
      groups: [group],
      gti: group.gti,
      titleName: group.titleName,
    });
  });
  return Array.from(sections.values());
}

function getDirtySummary (
  editorSchedules: ReadonlyMap<string, Schedule>,
  groupsById: ReadonlyMap<string, BulkScheduleGroup>,
): DirtySummary {
  const schedules = new Map<string, Schedule>();
  let assetCount = 0;
  editorSchedules.forEach((schedule, groupId) => {
    const group = groupsById.get(groupId);
    if (!group || !isScheduleDirty(group, schedule)) return;
    schedules.set(groupId, { ...schedule });
    assetCount += group.assetKeys.length;
  });
  return { assetCount, schedules };
}

export const BulkScheduleOverrideModal: React.FC<
BulkScheduleOverrideModalProps
> = ({
  open,
  groups,
  onApply,
  onClose,
}): React.ReactElement => {
  const { t } = useTranslation();
  const [editorSchedules, setEditorSchedules] = React.useState(
    () => createEditorSchedules(groups),
  );
  const [expandedGroupIds, setExpandedGroupIds] = React.useState<Set<string>>(
    () => new Set(),
  );
  const groupsById = React.useMemo(
    () => new Map(groups.map((group) => [group.groupId, group])),
    [groups],
  );
  const titleSections = React.useMemo(() => groupByTitle(groups), [groups]);
  const dirtySummary = React.useMemo(
    () => getDirtySummary(editorSchedules, groupsById),
    [editorSchedules, groupsById],
  );
  const hasValidationErrors = React.useMemo(
    () => Array.from(editorSchedules.entries()).some(([groupId, schedule]) => {
      const group = groupsById.get(groupId);
      if (!group) return false;
      const isDirty = isScheduleDirty(group, schedule);
      return getScheduleValidationError(schedule, isDirty) !== undefined;
    }),
    [editorSchedules, groupsById],
  );

  React.useEffect(() => {
    if (open) {
      setEditorSchedules(createEditorSchedules(groups));
      setExpandedGroupIds(new Set());
    }
  }, [groups, open]);

  const updateGroup = (
    groupId: string,
    field: keyof Schedule,
    value: string,
  ): void => {
    setEditorSchedules((current) => {
      const previous = current.get(groupId);
      if (!previous) return current;
      const next = new Map(current);
      next.set(groupId, {
        endTime: field === 'endTime' ? value : previous.endTime,
        startTime: field === 'startTime' ? value : previous.startTime,
      });
      return next;
    });
  };

  const resetGroup = (group: BulkScheduleGroup): void => {
    setEditorSchedules((current) => {
      const next = new Map(current);
      next.set(group.groupId, getInitialSchedule(group));
      return next;
    });
  };

  const toggleGroup = (groupId: string): void => {
    setExpandedGroupIds((current) => {
      const next = new Set(current);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const handleApply = (): void => {
    onApply(dirtySummary.schedules);
    onClose();
  };

  return (
    <Modal
      data-testid="bulk-schedule-override-modal"
      open={open}
      scrollContainer="viewport"
      title={t('asset_schedule_bulk_modal_title')}
      width="800px"
      onClose={onClose}
    >
      <Column
        alignmentHorizontal="start"
        data-testid="bulk-schedule-title-sections"
        heights="fit"
        spacing="300"
        width="100%"
      >
        <Text
          color="secondary"
          data-testid="bulk-schedule-utc-note"
          type="b100"
        >
          {t('asset_schedule_bulk_all_times_utc')}
        </Text>
        {titleSections.map((section) => (
          <BulkScheduleTitleSectionView
            editorSchedules={editorSchedules}
            expandedGroupIds={expandedGroupIds}
            key={section.gti}
            section={section}
            onChange={updateGroup}
            onReset={resetGroup}
            onToggle={toggleGroup}
          />
        ))}
      </Column>

      <ModalFooter>
        <Column spacing="200" width="100%">
          <Text
            color="secondary"
            data-testid="bulk-schedule-update-summary"
            type="b100"
          >
            {t('asset_schedule_bulk_update_summary', {
              assets: dirtySummary.assetCount,
              groups: dirtySummary.schedules.size,
            })}
          </Text>
          <Row alignmentHorizontal="end" spacing="200">
            <Button
              data-testid="bulk-schedule-cancel-button"
              size="small"
              type="secondary"
              onClick={onClose}
            >
              {t('buttons_cancel')}
            </Button>
            <Button
              data-testid="bulk-schedule-apply-button"
              disabled={dirtySummary.schedules.size === 0 || hasValidationErrors}
              size="small"
              type="primary"
              onClick={handleApply}
            >
              {t('asset_schedule_bulk_apply')}
            </Button>
          </Row>
        </Column>
      </ModalFooter>
    </Modal>
  );
};
